import { useState, useCallback } from "react";

/**
 * useColumnToggle
 *
 * Reusable hook for column visibility toggling.
 * Persists hidden columns to localStorage so preferences survive navigation.
 *
 * storageKey — unique key per page, e.g. "arc_cols_testsuite"
 *
 * Usage:
 *   const { visibleColumns, columnToggleProps } = useColumnToggle(
 *     columns,
 *     ["createdBy", "createdDate", "modifiedBy", "modifiedDate"],
 *     "arc_cols_testsuite"
 *   );
 */
export function useColumnToggle(
  allColumns,
  optionalKeys = [],
  storageKey = "arc_cols_default",
) {
  const [hiddenKeys, setHiddenKeys] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const updateHidden = useCallback(
    (keys) => {
      setHiddenKeys(keys);
      try {
        localStorage.setItem(storageKey, JSON.stringify(keys));
      } catch {
        // localStorage unavailable — fail silently
      }
    },
    [storageKey],
  );

  const toggleColumn = useCallback(
    (key) => {
      setHiddenKeys((prev) => {
        const next = prev.includes(key)
          ? prev.filter((k) => k !== key)
          : [...prev, key];
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {}
        return next;
      });
    },
    [storageKey],
  );

  const showAll = useCallback(() => updateHidden([]), [updateHidden]);
  const hideAll = useCallback(
    () => updateHidden(optionalKeys),
    [optionalKeys, updateHidden],
  );

  const visibleColumns = allColumns.filter(
    (col) => !hiddenKeys.includes(col.dataIndex),
  );

  const optionalColumns = allColumns.filter((col) =>
    optionalKeys.includes(col.dataIndex),
  );

  return {
    visibleColumns,
    columnToggleProps: {
      optionalColumns,
      hiddenKeys,
      toggleColumn,
      showAll,
      hideAll,
    },
  };
}
